import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Leaf, TrendUp, Calendar, Drop, Sun, Lightning, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { HiTexProductsFooter } from '../components/HiTexProductsFooter';
import { InnerCircleCTA } from '../components/InnerCircleCTA';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LanguageSelector } from '../components/LanguageSelector';
import { SEO } from '../components/SEO';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { OrganizationJsonLd } from '../components/OrganizationJsonLd';
import { WebSiteJsonLd } from '../components/WebSiteJsonLd';
import { SoftwareApplicationJsonLd } from '../components/SoftwareApplicationJsonLd';
import axios from 'axios';

import { API_URL } from '../config/api';
import { AmazonDisclosure } from '../components/AmazonDisclosure';

// MAINTENANCE MODE - Set to true to block login/register CTAs
const MAINTENANCE_MODE = false;

export const HomePage = () => {
  const { user, loading, login, register, hasPasskey, loginWithPasskey } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const [showAuth, setShowAuth] = useState(false);
  const [products, setProducts] = useState([]);
  const [authTab, setAuthTab] = useState('login');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [magicEmail, setMagicEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  // Passkey availability for the entered email — drives the "Use Face ID" button
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  // Probe the backend whenever the email changes (debounced) so the
  // biometric button shows up as soon as the user types a known address.
  useEffect(() => {
    if (!loginEmail || !loginEmail.includes('@')) {
      setPasskeyAvailable(false);
      return;
    }
    const t = setTimeout(async () => {
      const ok = await hasPasskey(loginEmail);
      setPasskeyAvailable(ok);
    }, 400);
    return () => clearTimeout(t);
  }, [loginEmail, hasPasskey]);

  // Session-expired flow — auto-open auth modal + remember where to return after login.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);

  // Post-auth checkout intent — when an anonymous user clicks "Become a Founding
  // Member" we send them to /?auth=1&intent=inner-circle. After they sign up
  // we honor that intent by POSTing to /api/payments/inner-circle/checkout and
  // redirecting to Stripe — instead of dumping them on the dashboard with no
  // path to actually pay. (Same hook can carry future paid-tier intents.)
  const checkoutIntentRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') === '1') setShowAuth(true);
    if (params.get('reason') === 'expired') setSessionExpired(true);
    const next = params.get('next');
    if (next) setNextUrl(decodeURIComponent(next));
    const intent = params.get('intent');
    if (intent) {
      checkoutIntentRef.current = intent;
      // Persist to sessionStorage so the intent survives the HomePage →
      // WelcomeHubPage unmount that happens the moment auth completes.
      // The HomeOrHub wrapper in App.js consumes this on mount.
      try { sessionStorage.setItem('turfpulse_post_auth_intent', intent); } catch (e) { /* private mode — ignore */ }
    }
  }, []);
  
  // Register form
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [grassType, setGrassType] = useState('cool-season');

  // Quiz funnel state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const quizQuestions = [
    {
      id: 'goal',
      question: "What's your main lawn goal?",
      options: [
        { value: 'thicker', label: 'Thicker, denser turf', icon: '1' },
        { value: 'weeds', label: 'Fewer weeds', icon: '2' },
        { value: 'timing', label: 'Better application timing', icon: '3' },
        { value: 'all', label: 'All of the above', icon: '4' },
      ]
    },
    {
      id: 'grass',
      question: "What type of grass do you have?",
      options: [
        { value: 'cool', label: 'Cool-season (Fescue, Bluegrass, Rye)', icon: '1' },
        { value: 'warm', label: 'Warm-season (Bermuda, Zoysia, St. Aug)', icon: '2' },
        { value: 'unsure', label: "Not sure yet", icon: '3' },
      ]
    },
    {
      id: 'experience',
      question: "How often have you applied a PGR or pre-emergent?",
      options: [
        { value: 'never', label: "Never — I'm brand new to this", icon: '1' },
        { value: 'some', label: 'A few times, still learning', icon: '2' },
        { value: 'experienced', label: "Regularly — I know what I'm doing", icon: '3' },
      ]
    },
    {
      id: 'scale',
      question: "How many properties or zones do you manage?",
      options: [
        { value: 'one', label: 'Just my yard', icon: '1' },
        { value: 'few', label: '2-3 properties', icon: '2' },
        { value: 'many', label: '4+ properties or zones', icon: '3' },
      ]
    },
  ];

  const getQuizRecommendation = () => {
    const { experience, scale } = quizAnswers;
    if (scale === 'many' || (experience === 'experienced' && scale === 'few')) {
      return 'enthusiast';
    }
    if (experience === 'experienced' || scale === 'few') {
      return 'homeowner';
    }
    return 'free';
  };

  const quizRecommendations = {
    free: {
      tier: 'Free',
      price: '$0',
      tagline: "Welcome to smarter lawn care — we'll guide you through it.",
      description: "You're just getting started, and that's exactly where we come in. TurfPulse watches your local weather and tells you when it's time to treat your lawn — no guessing, no confusing charts.",
      cta: 'Start Growing',
      features: ['GDD tracking — know exactly where your season stands', 'Daily weather + soil temperature at 4 depths', 'Seasonal calendar with treatment windows', 'Curated video guides from top creators', '1 property, 1 zone'],
    },
    homeowner: {
      tier: 'Homeowner Seasonal',
      price: '$50/season',
      tagline: "You've got the basics — now unlock the tools that save you time and money.",
      description: "You know what a pre-emergent is. Now you need the Mix Calculator, Spray Forecast, full spray history, and multi-zone tracking. Includes 1 month free trial.",
      cta: 'Start Free Trial',
      features: ['Everything in Free', 'Mix Calculator — exact grams, oz, scoops per gallon', '5-Day Spray Forecast — wind, humidity, temp scoring', 'Full spray log history (unlimited)', 'Equipment log & spreader settings', 'Up to 3 properties, 5 zones each'],
    },
    enthusiast: {
      tier: 'Enthusiast Annual',
      price: '$80/year',
      tagline: "You're managing real turf. Get the full toolkit.",
      description: "Multiple properties, regular PGR apps, soil testing — you're not casual anymore. Enthusiast gives you unlimited everything. Just $6.66/month.",
      cta: 'Start Free Trial',
      features: ['Everything in Homeowner', 'Unlimited properties & zones', 'Soil analysis & recommendations', 'PDF season reports', 'Personal weather station integration', 'Priority support'],
    },
  };

  useEffect(() => {
    if (!loading && user && user !== false) {
      // NOTE: post-auth Inner Circle checkout intent is now consumed by
      // HomeOrHub in App.js — that wrapper survives the user-state flip
      // that unmounts HomePage, so it can reliably honor the intent.
      // For the default-flow user we land them directly on /pulse — the
      // mobile-first dashboard is the primary daily-driver view. PWA
      // home-screen icon also opens here (manifest.start_url = /pulse).
      navigate('/pulse', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/products`);
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const result = await login(loginEmail, loginPassword);
    if (!result.success) {
      setError(result.error);
    } else {
      setShowAuth(false);
      // If user was bounced here by session expiry, return them to where they were.
      if (nextUrl) {
        setNextUrl(null);
        window.location.href = nextUrl; // full reload so fresh session token flows to all tabs
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    const result = await register(regEmail, regPassword, regName, grassType);
    if (!result.success) {
      setError(result.error);
    } else {
      setShowAuth(false);
      if (nextUrl) {
        setNextUrl(null);
        window.location.href = nextUrl;
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      console.error('Forgot password error:', error);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setMagicLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/magic-link`, { email: magicEmail });
      setMagicSent(true);
    } catch (error) {
      console.error('Magic link error:', error);
    } finally {
      setMagicLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleCTAClick = () => {
    if (MAINTENANCE_MODE) {
      alert('Coming Soon! We\'re making some exciting improvements to TurfPulse. Check back shortly!');
      return;
    }
    setShowAuth(true);
  };

  if (user && user !== false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Smart Lawn Care Powered by Growing Degree Days"
        description="Stop guessing when to spray, water, and treat your lawn. TurfPulse uses real weather science to tell you exactly when your turf needs attention. Free GDD tracker for bermuda, zoysia, fescue, and bluegrass."
        keywords="growing degree days, GDD tracker, GDD lawn care, GDD calculator, lawn care app, PGR application timing, when to apply pre-emergent, bermuda grass GDD, smart lawn care, turf management, weed control timing"
        path="/"
      />
      <FaqJsonLd items={[
        {
          q: "What is a Growing Degree Day (GDD)?",
          a: "A Growing Degree Day is a measure of heat accumulation used to predict plant and pest development. TurfPulse tracks GDD against your exact ZIP so you time PGR, pre-emergent, and fertilizer applications by the math, not by a generic calendar date.",
        },
        {
          q: "Is TurfPulse free?",
          a: "Yes — the core GDD tracker, Water Budget calculator, catch-cup tool, resistance tracker, and BYOS personal weather station integration are all free with no credit card. A paid tier unlocks Moe (our AI lawn assistant), advanced disease models, and multi-property support.",
        },
        {
          q: "Does TurfPulse work with warm-season grasses like bermuda and zoysia?",
          a: "Yes. TurfPulse was built in Round Rock, Texas and supports bermuda, zoysia, St. Augustine, centipede, bahia, buffalograss, and all cool-season types (KBG, fescue, ryegrass). Each grass has its own base temperature for GDD calculation.",
        },
        {
          q: "What makes TurfPulse different from other lawn apps?",
          a: "Most lawn apps push static calendars and generic product recommendations. TurfPulse runs real agronomic models — Smith-Kerns dollar-spot probability, Large Patch risk, rate-weighted PGR GDD, catch-cup-calibrated ET0 water budgets — the same math golf course superintendents use, but surfaced for homeowners obsessed with their own lawn.",
        },
        {
          q: "Can I connect my Rachio smart sprinkler?",
          a: "Yes. Paste your Rachio Personal Access Token once and TurfPulse reads your zones live. The Rachio Translator then converts any Water Budget output into a Rachio-style Program A/B/C cheat sheet you can enter in the Rachio app in under a minute.",
        },
      ]} />

      {/* Entity-cluster + rich-result schemas (TurfPulse ↔ Hi Tex Lawn Pro LLC ↔ afincalc) */}
      <OrganizationJsonLd />
      <WebSiteJsonLd />
      <SoftwareApplicationJsonLd />
      
      {/* Language Selector */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector variant="compact" />
      </div>
      
      {/* ===== HERO — Problem-First ===== */}
      <main id="main-content">
      <section className="relative min-h-[90vh] sm:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster="https://images.pexels.com/photos/26953738/pexels-photo-26953738.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920"
            className="absolute inset-0 w-full h-full object-cover"
            onLoadedData={(e) => e.target.play().catch(() => {})}
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6" data-testid="hero-brand-mark">
              <div className="drop-shadow-[0_0_18px_rgba(34,197,94,0.35)]">
                <TurfPulseLogoSVG animated={true} size={72} />
              </div>
              <span className="uppercase text-[10px] sm:text-xs tracking-[0.15em] font-medium text-white/70">TurfPulse</span>
            </div>
            
            <h1 data-testid="hero-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white leading-tight">
              Your lawn has math.<br />
              <span className="font-bold">We do it.</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-white/85 mb-8 leading-relaxed max-w-xl">
              TurfPulse pulls your local weather, soil temp, and Growing Degree Days every hour — then tells you the exact day to spray, water, or fertilize. Built in Round Rock, TX for enthusiasts who are done guessing.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                data-testid="get-started-btn"
                size="lg" 
                onClick={handleCTAClick}
                className="bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-6 text-base sm:text-lg rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg font-bold w-full sm:w-auto"
              >
                Get TurfPulse Free
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
              <Button 
                data-testid="quiz-cta-btn"
                size="lg" 
                variant="outline"
                onClick={() => { setShowQuiz(true); setQuizStep(0); setQuizAnswers({}); }}
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base sm:text-lg rounded-xl backdrop-blur-sm w-full sm:w-auto"
              >
                Which plan fits me?
              </Button>
            </div>
            
            <div className="mt-5">
              <button
                data-testid="try-free-diagnosis-link"
                onClick={() => navigate('/try')}
                className="group inline-flex items-center gap-2 text-sm text-white/90 hover:text-white underline decoration-white/30 decoration-dotted underline-offset-4 transition-colors"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
                </span>
                Snap a lawn photo — get an instant AI diagnosis
                <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>

            <p className="text-xs text-white/50 mt-4">Free forever. No credit card. No upsells.</p>

            {/* New-here pointer — soft, calm CTA for first-time visitors who
                want a guided walkthrough before committing. Lives just under
                the existing free-forever microcopy. */}
            <Link
              to="/getting-started"
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-300/90 hover:text-emerald-200 underline-offset-2 hover:underline"
              data-testid="home-new-here-link"
            >
              New here? Read the 5-minute starter guide →
            </Link>

            {/* Cited-by trust strip — conservative, can't go stale.
                Replaces the unverified "Biggest Update Yet" announcement banner
                with sources that are actually peer-reviewed. */}
            <div
              className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-lg border border-white/15 bg-white/5 backdrop-blur-sm px-4 py-2.5 text-[11px] text-white/70"
              data-testid="home-cited-by-strip"
            >
              <span className="font-bold uppercase tracking-widest text-white/60">Math we cite:</span>
              <a
                href="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0194216"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white underline-offset-2 hover:underline"
              >
                Smith-Kerns 2018
              </a>
              <span className="text-white/30">·</span>
              <a
                href="https://aggieturf.tamu.edu/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white underline-offset-2 hover:underline"
              >
                TAMU AgriLife
              </a>
              <span className="text-white/30">·</span>
              <a
                href="https://www.fao.org/4/x0490e/x0490e00.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white underline-offset-2 hover:underline"
              >
                FAO-56
              </a>
              <span className="text-white/30">·</span>
              <Link
                to="/credits"
                className="font-semibold text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
                data-testid="home-cited-by-credits-link"
              >
                See all sources →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== INNER CIRCLE RIBBON — Founding-member offer (10 seats) ===== */}
      <section
        className="relative border-y border-emerald-200/60 bg-gradient-to-r from-emerald-50 via-amber-50/40 to-emerald-50 dark:from-emerald-950/40 dark:via-amber-950/20 dark:to-emerald-950/40 px-4 sm:px-8 py-8 sm:py-10"
        data-testid="home-inner-circle-ribbon"
      >
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
          <div className="flex-shrink-0">
            <img
              src="/brand/turfpulse-inner-circle.svg"
              alt="TurfPulse Inner Circle crest"
              className="h-16 w-16 sm:h-20 sm:w-20"
              data-testid="home-inner-circle-crest"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-white">
              Founding · 10 seats only
            </div>
            <h3 className="mt-2 text-xl sm:text-2xl font-black text-zinc-900 dark:text-zinc-50 leading-tight">
              Become a TurfPulse Inner Circle founding member
            </h3>
            <p className="mt-1.5 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
              Card on file today, <strong>$0 charged</strong>. First charge of $4.99 hits Nov 1, 2026 — locked at that rate forever. Direct line to the founder, founding-member crest, full closed-loop irrigation engine.
            </p>
          </div>
          <div className="flex-shrink-0 flex flex-col items-stretch sm:items-end gap-2">
            <InnerCircleCTA testId="home-inner-circle-cta" size="default" />
            <a
              href="/pricing"
              className="text-xs text-zinc-600 dark:text-zinc-400 hover:text-emerald-700 dark:hover:text-emerald-400 underline text-center sm:text-right"
              data-testid="home-inner-circle-pricing-link"
            >
              See full pricing →
            </a>
          </div>
        </div>
      </section>

      {/* ===== THE BRIDGE — Why Rachio can't read Ecowitt (FB pain point) ===== */}
      <section
        className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-4 sm:px-8 py-12 sm:py-16"
        data-testid="home-bridge-section"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 dark:bg-amber-900/40 px-3 py-1 text-xs font-bold text-amber-800 dark:text-amber-200">
              The problem nobody on Facebook can solve
            </div>
            <h2 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-black text-zinc-900 dark:text-zinc-50 leading-tight">
              Your Rachio can't read your Ecowitt sensors.<br className="hidden sm:block" />
              <span className="text-emerald-700 dark:text-emerald-400">TurfPulse is the bridge.</span>
            </h2>
          </div>

          {/* The 3-box explainer */}
          <div className="grid gap-4 sm:gap-5 sm:grid-cols-3">
            {/* Box 1: Ecowitt */}
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40 p-5 text-center">
              <div className="text-3xl mb-2">🌱</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400">Step 1</div>
              <div className="mt-1 font-bold text-zinc-900 dark:text-zinc-50">Ecowitt soil probes</div>
              <p className="mt-2 text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Buried in your zones. Read soil moisture every 16 minutes, 24/7.
                <strong> No weather station required.</strong>
              </p>
            </div>
            {/* Box 2: TurfPulse — highlighted */}
            <div className="rounded-xl border-2 border-emerald-500 dark:border-emerald-500 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/40 dark:to-zinc-950 p-5 text-center shadow-md">
              <div className="text-3xl mb-2">🧠</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300">Step 2 · the bridge</div>
              <div className="mt-1 font-black text-zinc-900 dark:text-zinc-50">TurfPulse</div>
              <p className="mt-2 text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Reads your probes. Applies the math. Runs the skip-gates.
                <strong> The brain for your Rachio.</strong>
              </p>
            </div>
            {/* Box 3: Rachio */}
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40 p-5 text-center">
              <div className="text-3xl mb-2">💧</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400">Step 3</div>
              <div className="mt-1 font-bold text-zinc-900 dark:text-zinc-50">Your Rachio</div>
              <p className="mt-2 text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Fires the zones we tell it to, only when the soil agrees.
                Your existing controller — no replacement needed.
              </p>
            </div>
          </div>

          {/* The honest framing */}
          <div className="mt-8 max-w-3xl mx-auto text-center text-sm sm:text-base text-zinc-700 dark:text-zinc-300 leading-relaxed">
            <p>
              Rachio doesn't have a native Ecowitt integration. Ecowitt doesn't talk to Rachio.
              Half the threads on the Rachio Facebook group are people trying to wire up an Arduino,
              IFTTT chains, or Home Assistant scripts to make this work — and most give up.
            </p>
            <p className="mt-3 font-semibold text-zinc-900 dark:text-zinc-50">
              We did the wiring. You don't have to.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/ecowitt-to-rachio"
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 hover:bg-emerald-800 px-4 py-2 text-sm font-semibold text-white"
              data-testid="home-bridge-cta-explainer"
            >
              See the full Ecowitt → Rachio breakdown →
            </Link>
            <Link
              to="/getting-started"
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-900"
              data-testid="home-bridge-cta-start"
            >
              Read the 5-minute starter guide
            </Link>
          </div>
        </div>
      </section>

      {/* ===== THREE PROBLEMS — The pain they feel ===== */}
      <section className="py-16 sm:py-24 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">Three questions every lawn owner asks</h2>
            <p className="text-muted-foreground mt-3 text-base sm:text-lg max-w-2xl mx-auto">TurfPulse answers all of them — automatically, based on your actual weather.</p>
          </motion.div>
          
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="bg-card border rounded-2xl p-6 sm:p-8 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-5">
                <Calendar weight="duotone" size={28} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-bold mb-2">"When should I spray?"</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Not based on the calendar. Based on accumulated heat in YOUR yard. We track Growing Degree Days so your timing is dialed in — the same science golf courses use.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card border-2 border-primary/30 rounded-2xl p-6 sm:p-8 text-center relative"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider">
                Most asked
              </div>
              <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-5">
                <Drop weight="duotone" size={28} className="text-blue-600" />
              </div>
              <h3 className="text-lg font-bold mb-2">"Am I watering enough?"</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We measure what your lawn loses to evaporation vs. what it gets from rain. If there's a deficit, we tell you how many inches to put down. No guessing.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card border rounded-2xl p-6 sm:p-8 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-5">
                <TrendUp weight="duotone" size={28} className="text-green-600" />
              </div>
              <h3 className="text-lg font-bold mb-2">"Am I wasting money?"</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Spraying on the wrong day or at the wrong time means wasted product. We check wind, rain, humidity, and temperature before you ever open a bottle.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS — Dead simple ===== */}
      <section className="py-16 sm:py-24 px-4 sm:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">How it works</h2>
            <p className="text-muted-foreground mt-3 text-base sm:text-lg">Three steps. That's it.</p>
          </motion.div>
          
          <div className="space-y-6 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-8">
            {[
              { step: '1', title: 'Set your location', desc: 'Tell us where your lawn is. We start watching your weather immediately.' },
              { step: '2', title: 'We do the math', desc: 'GDD tracking, soil moisture, rainfall, spray conditions — all calculated from real data at your address.' },
              { step: '3', title: 'You get told what to do', desc: '"Hey, your lawn needs water." "Spray window opens Thursday." "PGR due in 3 days." Simple.' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="flex sm:flex-col items-start gap-4"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-bold text-base sm:text-lg mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Button 
              size="lg" 
              onClick={handleCTAClick}
              className="bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-5 text-base sm:text-lg rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg font-bold"
              data-testid="how-it-works-cta-btn"
            >
              Try It Free
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ===== WHAT YOU GET — Not features, outcomes ===== */}
      <section className="py-16 sm:py-24 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">What you actually get</h2>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {[
              { icon: Sun, text: 'Daily dashboard that tells you what your lawn needs today', color: 'text-amber-600' },
              { icon: Drop, text: 'Rainfall tracking + watering deficit calculator', color: 'text-blue-600' },
              { icon: Calendar, text: 'Spray day forecasts — wind, rain, humidity scored', color: 'text-emerald-600' },
              { icon: TrendUp, text: 'GDD-based application timing (PGR, pre-emergent, pest)', color: 'text-violet-600' },
              { icon: Leaf, text: 'Full spray log history with weather conditions captured', color: 'text-green-600' },
              { icon: CheckCircle, text: 'Editorial product picks — no pay-to-place, no house brand', color: 'text-primary' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -10 : 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                viewport={{ once: true }}
                className="flex items-start gap-3 p-3 sm:p-4 rounded-xl hover:bg-muted/50 transition-colors"
              >
                <item.icon weight="duotone" size={22} className={`${item.color} flex-shrink-0 mt-0.5`} />
                <span className="text-sm sm:text-base">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== THE HONEST DIFFERENCE — Scott Leese style ===== */}
      <section className="py-16 sm:py-24 px-4 sm:px-8 bg-secondary/30">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8">Why people switch to TurfPulse</h2>
            
            <div className="space-y-6">
              <div className="bg-card border rounded-xl p-5 sm:p-6">
                <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                  <span className="font-semibold text-foreground">Most lawn apps are storefronts in disguise.</span> Their "recommendations" push you toward products they sell. When the app recommending a product also profits from selling it — is that recommendation for your lawn or their bottom line?
                </p>
              </div>
              
              <div className="bg-card border-2 border-primary/20 rounded-xl p-5 sm:p-6">
                <p className="text-sm sm:text-base leading-relaxed">
                  <span className="font-semibold text-primary">TurfPulse doesn't sell its own products.</span> We're not a manufacturer or retailer. When we recommend something, it's because it pairs with a feature we built — and we link to Amazon, where you were probably shopping anyway. We earn a small affiliate commission at no extra cost to you. Our job stays the same: tell you <strong>when</strong> to treat and <strong>whether conditions are right</strong>. You pick the products. We give you the timing.
                </p>
              </div>
              
              <p className="text-xs text-center text-muted-foreground italic px-4">
                "I used to just throw down Scotts on Memorial Day and hope for the best. Now I actually know when my lawn needs it."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== OUR STORY — Short, real, relatable ===== */}
      <section className="py-16 sm:py-24 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-4">Our Story</p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-6">Built by a homeowner who got tired of guessing</h2>
            <div className="space-y-4 text-muted-foreground text-base leading-relaxed">
              <p>
                It started with just cutting grass. Then YouTube rabbit holes turned into pre-emergent programs, PGR schedules, and spray calibrations. I couldn't find a single tool that tracked it all — so I built one.
              </p>
              <p>
                TurfPulse isn't backed by a fertilizer company or built by people who've never mowed a lawn. It's built by someone who stands in his yard every Saturday morning, just like you.
              </p>
            </div>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/about')}
              className="mt-6"
              data-testid="landing-read-story-btn"
            >
              Read the Full Story
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ===== Affiliate Products Marquee ===== */}
      {products.length > 0 && (
        <section className="py-16 bg-secondary/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 mb-8">
            <div className="text-center">
              <span className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Products We Trust</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-3">Lawn Care Products Worth Your Money</h2>
            </div>
            {/* FTC-required disclosure positioned ABOVE the affiliate marquee */}
            <div className="mt-4 max-w-2xl mx-auto rounded-md bg-background/60 border border-border/60 px-3 py-2">
              <AmazonDisclosure variant="inline" />
            </div>
          </div>
          
          <Marquee gradient={false} speed={40} className="py-6">
            {products.map((product, idx) => (
              <a
                key={product.id || idx}
                href={product.affiliate_link}
                target="_blank"
                rel="nofollow sponsored noopener noreferrer"
                className="mx-4 flex flex-col items-center justify-center bg-card border border-black/5 rounded-xl p-5 w-56 hover:shadow-lg transition-shadow duration-300"
              >
                {product.image_url && (
                  <img src={product.image_url} alt={product.name} className="w-full h-36 object-cover rounded-lg mb-3" />
                )}
                <h4 className="font-bold text-center text-sm mb-1">{product.name}</h4>
                {product.mark_text && (
                  <span className="text-[10px] uppercase tracking-wider text-accent font-medium bg-accent/10 px-2 py-0.5 rounded-full">
                    {product.mark_text}
                  </span>
                )}
              </a>
            ))}
          </Marquee>
        </section>
      )}

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 sm:py-24 px-4 sm:px-8 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Freshly mowed residential lawn"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1635108201275-b1863fcb9eeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBsYXduJTIwbW93aW5nJTIwc3RyaXBlcyUyMGhvdXNlJTIweWFyZHxlbnwwfHx8fDE3NzUyMjI3MjZ8MA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          <div className="absolute inset-0 bg-primary/80"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-primary-foreground">Your lawn is already telling you something.</h2>
          <p className="text-base sm:text-lg mb-8 text-primary-foreground/90 max-w-xl mx-auto">
            Sign up free. Set your location. Let TurfPulse translate your weather into action.
          </p>
          <Button 
            data-testid="cta-get-started-btn"
            size="lg"
            onClick={handleCTAClick}
            className="bg-accent-foreground text-accent hover:bg-accent-foreground/90 px-10 py-6 text-base sm:text-lg rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg font-bold"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      </main>

      {/* Quiz Funnel Dialog */}
      <Dialog open={showQuiz} onOpenChange={setShowQuiz}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="quiz-dialog">
          {quizStep <= quizQuestions.length - 1 ? (
            <div className="p-6 sm:p-8">
              <div className="flex gap-1.5 mb-6">
                {quizQuestions.map((_, i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= quizStep ? 'bg-primary' : 'bg-muted'}`} />
                ))}
              </div>

              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Question {quizStep + 1} of {quizQuestions.length}</p>
              <h3 className="text-xl sm:text-2xl font-bold mb-6">{quizQuestions[quizStep].question}</h3>

              <div className="space-y-3">
                {quizQuestions[quizStep].options.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`quiz-option-${opt.value}`}
                    onClick={() => {
                      const updated = { ...quizAnswers, [quizQuestions[quizStep].id]: opt.value };
                      setQuizAnswers(updated);
                      if (quizStep < quizQuestions.length - 1) {
                        setQuizStep(quizStep + 1);
                      } else {
                        setQuizStep(quizQuestions.length);
                      }
                    }}
                    className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all duration-200 hover:border-primary hover:bg-primary/5 ${
                      quizAnswers[quizQuestions[quizStep]?.id] === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border'
                    }`}
                  >
                    <span className="text-sm font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>

              {quizStep > 0 && (
                <button onClick={() => setQuizStep(quizStep - 1)} className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Back
                </button>
              )}
            </div>
          ) : (
            (() => {
              const rec = quizRecommendations[getQuizRecommendation()];
              return (
                <div className="p-6 sm:p-8">
                  <div className="flex gap-1.5 mb-6">
                    {quizQuestions.map((_, i) => (
                      <div key={i} className="h-1.5 flex-1 rounded-full bg-primary" />
                    ))}
                  </div>

                  <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Your Recommendation</p>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-1">{rec.tier}</h3>
                  <p className="text-lg font-semibold text-primary mb-4">{rec.price}</p>

                  <div className="bg-muted/50 rounded-xl p-4 mb-4">
                    <p className="text-sm italic text-muted-foreground mb-2">{rec.tagline}</p>
                    <p className="text-sm">{rec.description}</p>
                  </div>

                  <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">What you get:</p>
                  <ul className="space-y-1.5 mb-6">
                    {rec.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Leaf size={14} weight="fill" className="text-primary flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    data-testid="quiz-result-cta"
                    size="lg"
                    onClick={() => { setShowQuiz(false); handleCTAClick(); }}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-5 text-base rounded-xl font-bold"
                  >
                    {rec.cta}
                  </Button>

                  <button onClick={() => { setQuizStep(0); setQuizAnswers({}); }} className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Retake quiz
                  </button>
                </div>
              );
            })()
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-8 bg-card border-t border-border" role="contentinfo">
        <div className="max-w-7xl mx-auto flex flex-col gap-4 items-center text-center">
          <HiTexProductsFooter />
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} TurfPulse. All rights reserved.
          </p>
          <nav aria-label="Footer navigation" className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
            <a href="/about" className="hover:text-foreground transition-colors">About</a>
            <a href="/kit" className="hover:text-foreground transition-colors" data-testid="footer-kit-link">Lab Starter Kit</a>
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-foreground transition-colors">Terms of Service</a>
            <a href="/accessibility" className="hover:text-foreground transition-colors">Accessibility</a>
            <a href="/disclosure" className="hover:text-foreground transition-colors">Affiliate Disclosure</a>
            <a href="/partners" className="hover:text-foreground transition-colors">Partners</a>
          </nav>
          <p className="text-xs text-muted-foreground">
            As an Amazon Associate, TurfPulse earns from qualifying purchases.
          </p>
          <div className="mt-4 pt-4 border-t border-border/50 max-w-3xl text-xs text-muted-foreground/70 space-y-2">
            <p>
              <strong>Disclaimer:</strong> TurfPulse is an informational tool for lawn care tracking and is not a substitute for professional advice. 
              Always read and follow product label directions. The applicator is solely responsible for compliance with all local, state, and federal regulations.
            </p>
            <p>
              Weather and GDD data are provided by third-party sources and accuracy is not guaranteed.
              TurfPulse does not manufacture or stock products; product recommendations link to third-party retailers (primarily Amazon) through affiliate partnerships — we earn a commission at no extra cost to you. TurfPulse is not responsible for product performance, availability, or misuse.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent data-testid="auth-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <TurfPulseLogoSVG animated={true} size={32} />
              TurfPulse
            </DialogTitle>
          </DialogHeader>

          {sessionExpired && (
            <div
              className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300"
              data-testid="session-expired-banner"
            >
              <strong>Your session expired.</strong> Sign in again and we'll take you right back
              {nextUrl ? ` to ${nextUrl}` : ''}.
            </div>
          )}
          
          <Tabs value={authTab} onValueChange={setAuthTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register">{t('auth.register')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" aria-label="Login form">
                {error && (
                  <div data-testid="error-message" role="alert" aria-live="assertive" className="bg-destructive/10 text-destructive p-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <Label htmlFor="login-email">{t('auth.email')}</Label>
                  <Input id="login-email" data-testid="login-email-input" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required aria-required="true" autoComplete="email" />
                </div>
                <div>
                  <Label htmlFor="login-password">{t('auth.password')}</Label>
                  <Input id="login-password" data-testid="login-password-input" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required aria-required="true" autoComplete="current-password" />
                </div>
                <Button data-testid="login-submit-btn" type="submit" className="w-full">{t('auth.login')}</Button>

                {/* Biometric quick-unlock — only renders when the entered
                    email has at least one passkey enrolled on the server.
                    Fires Face ID / Touch ID / Windows Hello prompt. */}
                {passkeyAvailable && (
                  <Button
                    data-testid="login-passkey-btn"
                    type="button"
                    variant="outline"
                    disabled={passkeyBusy}
                    onClick={async () => {
                      setError(''); setPasskeyBusy(true);
                      const res = await loginWithPasskey(loginEmail);
                      setPasskeyBusy(false);
                      if (res.success) {
                        setShowAuth(false);
                      } else if (res.error !== 'no_passkey') {
                        setError(res.error);
                      }
                    }}
                    className="w-full border-emerald-500/40 hover:bg-emerald-500/10"
                  >
                    {passkeyBusy ? 'Prompting…' : '🔓  Use Face ID / Touch ID'}
                  </Button>
                )}

                <div className="flex justify-between text-sm pt-2">
                  <button type="button" onClick={() => { setShowAuth(false); setShowForgotPassword(true); }} className="text-muted-foreground hover:text-primary underline" data-testid="forgot-password-link">
                    {t('auth.forgotPassword')}
                  </button>
                  <button type="button" onClick={() => { setShowAuth(false); setShowMagicLink(true); }} className="text-muted-foreground hover:text-primary underline" data-testid="magic-link-btn">
                    {t('auth.magicLink')}
                  </button>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" aria-label="Registration form">
                {error && (
                  <div data-testid="error-message" role="alert" aria-live="assertive" className="bg-destructive/10 text-destructive p-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <Label htmlFor="reg-name">{t('auth.name')}</Label>
                  <Input id="reg-name" data-testid="register-name-input" type="text" value={regName} onChange={(e) => setRegName(e.target.value)} required aria-required="true" autoComplete="name" />
                </div>
                <div>
                  <Label htmlFor="reg-email">{t('auth.email')}</Label>
                  <Input id="reg-email" data-testid="register-email-input" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required aria-required="true" autoComplete="email" />
                </div>
                <div>
                  <Label htmlFor="reg-password">{t('auth.password')}</Label>
                  <Input id="reg-password" data-testid="register-password-input" type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required aria-required="true" autoComplete="new-password" />
                </div>
                <div>
                  <Label htmlFor="grass-type">{t('auth.grassType')}</Label>
                  <Select value={grassType} onValueChange={setGrassType}>
                    <SelectTrigger data-testid="grass-type-select" aria-label="Select grass type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cool-season">{t('auth.coolSeason')}</SelectItem>
                      <SelectItem value="warm-season">{t('auth.warmSeason')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="register-submit-btn" type="submit" className="w-full">{t('auth.register')}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => { setShowForgotPassword(open); if (!open) { setForgotSent(false); setForgotEmail(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Reset Your Password</DialogTitle>
          </DialogHeader>
          {forgotSent ? (
            <div className="text-center py-4">
              <p className="text-muted-foreground">If an account exists with that email, we've sent a password reset link. Check your inbox!</p>
              <Button className="mt-4" onClick={() => { setShowForgotPassword(false); setForgotSent(false); setShowAuth(true); }}>Back to Login</Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-muted-foreground">Enter your email address and we'll send you a link to reset your password.</p>
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              <Button type="submit" className="w-full" disabled={forgotLoading} data-testid="forgot-submit-btn">{forgotLoading ? 'Sending...' : 'Send Reset Link'}</Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => { setShowForgotPassword(false); setShowAuth(true); }}>Back to Login</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Magic Link Dialog */}
      <Dialog open={showMagicLink} onOpenChange={(open) => { setShowMagicLink(open); if (!open) { setMagicSent(false); setMagicEmail(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Passwordless Login</DialogTitle>
          </DialogHeader>
          {magicSent ? (
            <div className="text-center py-4">
              <p className="text-muted-foreground">Magic link sent! Check your email and click the link to log in instantly. The link expires in 15 minutes.</p>
              <Button className="mt-4" onClick={() => { setShowMagicLink(false); setMagicSent(false); }}>Got it</Button>
            </div>
          ) : (
            <form onSubmit={handleMagicLink} className="space-y-4">
              <p className="text-sm text-muted-foreground">Enter your email and we'll send you a magic link. Click it to log in instantly — no password needed!</p>
              <div>
                <Label htmlFor="magic-email">Email</Label>
                <Input id="magic-email" type="email" value={magicEmail} onChange={(e) => setMagicEmail(e.target.value)} required data-testid="magic-email-input" />
              </div>
              <Button type="submit" className="w-full" disabled={magicLoading} data-testid="magic-submit-btn">{magicLoading ? 'Sending...' : 'Send Magic Link'}</Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => { setShowMagicLink(false); setShowAuth(true); }}>Back to Login</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
